(function () {
  const STORAGE_KEY = 'gebestProductCompare';
  const CART_ATTR_KEY = 'gebest_compare_products';
  const CART_META_KEY = 'gebest_compare_meta';

  function getConfig() {
    return window.gebestCompareConfig || {};
  }

  function getMaxProducts() {
    const max = parseInt(getConfig().maxProducts, 10);
    return Number.isFinite(max) && max > 0 ? max : 5;
  }

  function getCartUpdateUrl() {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}cart/update.js`;
  }

  function dedupeItems(items) {
    const seen = new Set();
    const result = [];

    items.forEach((raw) => {
      const item = normalizeItem(raw);
      if (!item || seen.has(item.handle)) return;
      seen.add(item.handle);
      result.push(item);
    });

    return result;
  }

  function readStorage() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? dedupeItems(parsed) : [];
    } catch (error) {
      return [];
    }
  }

  function writeStorage(items) {
    const deduped = dedupeItems(items);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(deduped));
    document.dispatchEvent(new CustomEvent('gebest:compare:updated', { detail: { items: deduped } }));
    syncToCart(deduped);
  }

  function serializeItemsForCart(items) {
    return items
      .map((item) => {
        const normalized = normalizeItem(item);
        if (!normalized) return '';
        return [normalized.handle, normalized.collectionHandle, normalized.collectionTitle].join('::');
      })
      .filter(Boolean)
      .join('|');
  }

  function getItemsForSync(handles) {
    const stored = readStorage();
    const storedMap = new Map(stored.map((item) => [item.handle, item]));
    return normalizeHandleList(handles).map((handle) => {
      if (storedMap.has(handle)) return storedMap.get(handle);
      return { id: '', handle, collectionHandle: '', collectionTitle: '' };
    });
  }

  function normalizeItem(item) {
    if (!item || !item.handle) return null;
    return {
      id: String(item.id || ''),
      handle: String(item.handle),
      collectionHandle: String(item.collectionHandle || ''),
      collectionTitle: String(item.collectionTitle || ''),
    };
  }

  function getComparePageUrl() {
    const configUrl = getConfig().comparePageUrl;
    if (configUrl) return configUrl;
    return '/pages/product-comparison';
  }

  function buildCompareUrl(handles) {
    const base = getComparePageUrl().split('?')[0];
    if (!handles.length) return base;
    return `${base}?products=${encodeURIComponent(handles.join(','))}`;
  }

  function parseProductsFromSearch(search) {
    if (!search) return [];
    const params = new URLSearchParams(search.replace(/^\?/, ''));
    const productsParam = params.get('products');
    if (!productsParam) return [];
    return productsParam
      .split(',')
      .map((handle) => decodeURIComponent(handle.trim()))
      .filter(Boolean);
  }

  function normalizeHandleList(handles) {
    return handles.map((handle) => String(handle).trim()).filter(Boolean);
  }

  function handlesMatch(left, right) {
    const a = normalizeHandleList(left);
    const b = normalizeHandleList(right);
    if (a.length !== b.length) return false;
    return a.every((handle, index) => handle === b[index]);
  }

  async function syncToCart(itemsOrHandles) {
    const items = Array.isArray(itemsOrHandles) && itemsOrHandles.length && itemsOrHandles[0]?.handle
      ? itemsOrHandles.map((item) => normalizeItem(item)).filter(Boolean)
      : getItemsForSync(itemsOrHandles || readStorage().map((item) => item.handle));

    const normalizedHandles = items.map((item) => item.handle);
    const handlesValue = normalizedHandles.join(',');
    const metaValue = serializeItemsForCart(items);

    try {
      const response = await fetch(getCartUpdateUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          attributes: {
            [CART_ATTR_KEY]: handlesValue,
            [CART_META_KEY]: metaValue,
          },
        }),
      });

      if (!response.ok) {
        throw new Error(`Cart sync failed: ${response.status}`);
      }

      return true;
    } catch (error) {
      console.warn('gebestsCompare: unable to sync cart attributes', error);
      return false;
    }
  }

  function updateCompareButtons() {
    const items = readStorage();
    const handles = new Set(items.map((item) => item.handle));

    document.querySelectorAll('.product-gebest__compare').forEach((button) => {
      const handle = button.dataset.productHandle;
      const isActive = handle && handles.has(handle);
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  function pruneStorageToRenderedProducts() {
    const section = document.querySelector('[data-product-compare-section]');
    if (!section) return;

    const validHandles = new Set(
      Array.from(section.querySelectorAll('[data-compare-column][data-product-handle]'))
        .map((el) => el.dataset.productHandle)
        .filter(Boolean)
    );

    if (!validHandles.size) return;

    const items = readStorage().filter((item) => validHandles.has(item.handle));
    const currentItems = readStorage();

    if (
      items.length !== currentItems.length
      || !handlesMatch(
        items.map((item) => item.handle),
        currentItems.map((item) => item.handle)
      )
    ) {
      writeStorage(items);
    }
  }

  function updateCompareBar() {
    const bar = document.querySelector('[data-gebest-compare-bar]');
    if (!bar) return;

    const count = readStorage().length;
    const countEl = bar.querySelector('[data-compare-count]');

    if (countEl) countEl.textContent = String(count);
    bar.hidden = count === 0;

    bar.querySelectorAll('[data-compare-link]').forEach((anchor) => {
      const handles = readStorage().map((item) => item.handle);
      anchor.href = buildCompareUrl(handles);
    });
  }

  function showMessage(message) {
    if (!message) return;
    if (typeof window.showAlert === 'function') {
      window.showAlert(message);
      return;
    }
    window.alert(message);
  }

  function applyCategoryData(items) {
    const itemMap = new Map(items.map((item) => [item.handle, item]));
    document.querySelectorAll('[data-compare-column]').forEach((column) => {
      const handle = column.dataset.productHandle;
      const item = itemMap.get(handle);
      if (!item) return;
      const collectionKey = item.collectionHandle || 'uncategorized';
      column.dataset.collectionHandle = collectionKey;
      column.dataset.collectionTitle = item.collectionTitle || '';
    });

    document.querySelectorAll('.product-compare__card[data-product-handle]').forEach((card) => {
      const handle = card.dataset.productHandle;
      const item = itemMap.get(handle);
      if (!item) return;
      card.dataset.collectionHandle = item.collectionHandle || 'uncategorized';
      card.dataset.collectionTitle = item.collectionTitle || '';
    });
  }

  function enrichItemsFromDom(items) {
    const sourceItems = items || readStorage();
    const columnMap = new Map();

    document.querySelectorAll('[data-compare-column][data-product-handle]').forEach((column) => {
      const handle = column.dataset.productHandle;
      const collectionHandle = (column.dataset.collectionHandle || '').trim();
      if (!handle || !collectionHandle || collectionHandle === 'uncategorized') return;

      const collectionTitle = (column.dataset.collectionTitle || '').trim();
      const existing = columnMap.get(handle);

      if (existing?.collectionTitle && !collectionTitle) return;

      columnMap.set(handle, {
        collectionHandle,
        collectionTitle: collectionTitle || existing?.collectionTitle || '',
      });
    });

    let changed = false;
    const enriched = sourceItems.map((item) => {
      if (item.collectionHandle) return item;

      const domMeta = columnMap.get(item.handle);
      if (!domMeta) return item;

      if (!domMeta.collectionTitle) {
        const titledColumn = document.querySelector(
          `[data-compare-column][data-collection-handle="${domMeta.collectionHandle}"][data-collection-title]`
        );
        if (titledColumn?.dataset.collectionTitle) {
          domMeta.collectionTitle = titledColumn.dataset.collectionTitle;
        }
      }

      changed = true;
      return {
        ...item,
        collectionHandle: domMeta.collectionHandle,
        collectionTitle: domMeta.collectionTitle,
      };
    });

    if (changed) {
      writeStorage(enriched);
      return enriched;
    }

    return sourceItems;
  }

  function initCompareScroll() {
    const scrollArea = document.querySelector('[data-compare-scroll-area]');
    const track = document.querySelector('[data-compare-products-track]');
    const nextButton = document.querySelector('[data-compare-scroll-next]');
    if (!track || track.dataset.compareScrollBound === 'true') return;
    track.dataset.compareScrollBound = 'true';

    const mobileQuery = window.matchMedia('(max-width: 767px)');
    const tableBlocks = Array.from(document.querySelectorAll('.product-compare-section__table-block'));

    const getScroller = () => (mobileQuery.matches && scrollArea ? scrollArea : track);

    let rafId = null;

    const syncTables = (scrollLeft) => {
      if (mobileQuery.matches) return;
      tableBlocks.forEach((block) => {
        if (Math.abs(block.scrollLeft - scrollLeft) > 0.5) {
          block.scrollLeft = scrollLeft;
        }
      });
    };

    const handleScroll = (source) => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = null;
        syncTables(source.scrollLeft);
      });
    };

    track.addEventListener('scroll', () => handleScroll(track), { passive: true });

    if (!nextButton) return;

    nextButton.addEventListener('click', () => {
      const scroller = getScroller();
      const column = scroller.querySelector('.product-compare-section__product-col');
      const grid = scroller.querySelector('.product-compare-section__products-grid');
      const styles = grid ? getComputedStyle(grid) : getComputedStyle(scroller);
      const gap = parseFloat(styles.gap || styles.columnGap) || 16;
      const amount = column ? column.offsetWidth + gap : scroller.clientWidth * 0.8;
      scroller.scrollBy({ left: amount, behavior: 'smooth' });
    });
  }

  function showCompareLoading(section) {
    const empty = section.querySelector('[data-compare-empty]');
    const loading = section.querySelector('[data-compare-loading]');
    const message = section.querySelector('[data-compare-empty-message]');
    const action = section.querySelector('[data-compare-empty-action]');

    if (empty) empty.hidden = false;
    if (loading) loading.hidden = false;
    if (message) message.hidden = true;
    if (action) action.hidden = true;
  }

  function getDesiredHandles() {
    const urlHandles = parseProductsFromSearch(window.location.search);
    if (urlHandles.length) return urlHandles;
    return readStorage().map((item) => item.handle);
  }

  function bindCompareButtonDelegation() {
    if (document.documentElement.dataset.gebestCompareDelegation === 'true') return;
    document.documentElement.dataset.gebestCompareDelegation = 'true';

    document.addEventListener('click', (event) => {
      const button = event.target.closest('.product-gebest__compare');
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();

      gebestsCompare.toggle({
        id: button.dataset.productId,
        handle: button.dataset.productHandle,
        collectionHandle: button.dataset.collectionHandle || '',
        collectionTitle: button.dataset.collectionTitle || '',
      });
    });
  }

  const gebestsCompare = {
    getItems() {
      return readStorage();
    },

    getCount() {
      return readStorage().length;
    },

    has(handle) {
      return readStorage().some((item) => item.handle === handle);
    },

    add(rawItem) {
      const item = normalizeItem(rawItem);
      if (!item) return false;

      const items = readStorage();
      if (items.some((entry) => entry.handle === item.handle)) {
        return true;
      }

      if (items.length >= getMaxProducts()) {
        const maxMessage = getConfig().maxProductsMessage;
        showMessage(
          maxMessage || `You can compare up to ${getMaxProducts()} products at a time.`
        );
        return false;
      }

      items.push(item);
      writeStorage(items);
      updateCompareButtons();
      updateCompareBar();
      return true;
    },

    remove(handle) {
      if (!handle) return;
      const items = readStorage().filter((item) => item.handle !== handle);
      writeStorage(items);
      updateCompareButtons();
      updateCompareBar();
    },

    toggle(rawItem) {
      const item = normalizeItem(rawItem);
      if (!item) return false;
      if (this.has(item.handle)) {
        this.remove(item.handle);
        return false;
      }
      return this.add(item);
    },

    clear() {
      writeStorage([]);
      updateCompareButtons();
      updateCompareBar();
    },

    async goToComparePage() {
      const items = readStorage();
      if (!items.length) return;
      await syncToCart(items);
      window.location.href = buildCompareUrl(items.map((item) => item.handle));
    },

    syncStorageFromUrl() {
      const handles = parseProductsFromSearch(window.location.search);
      if (!handles.length) return;

      const existing = readStorage();
      const existingMap = new Map(existing.map((item) => [item.handle, item]));
      const next = handles.map((handle) => {
        if (existingMap.has(handle)) return existingMap.get(handle);
        return { id: '', handle, collectionHandle: '', collectionTitle: '' };
      });

      writeStorage(next);
    },

    applyCategoryData(items) {
      applyCategoryData(items || readStorage());
    },

    enrichItemsFromDom(items) {
      return enrichItemsFromDom(items);
    },

    async removeAndReload(handle) {
      this.remove(handle);
      const items = readStorage();
      await syncToCart(items);
      const handles = items.map((item) => item.handle);
      const base = getComparePageUrl().split('?')[0];
      window.location.href = handles.length ? buildCompareUrl(handles) : base;
    },

    initButtons(root) {
      const scope = root || document;

      scope.querySelectorAll('[data-compare-remove]').forEach((button) => {
        if (button.dataset.compareRemoveBound === 'true') return;
        button.dataset.compareRemoveBound = 'true';

        button.addEventListener('click', (event) => {
          event.preventDefault();
          gebestsCompare.removeAndReload(button.dataset.compareRemove);
        });
      });

      scope.querySelectorAll('[data-compare-link]').forEach((link) => {
        if (link.dataset.compareLinkBound === 'true') return;
        link.dataset.compareLinkBound = 'true';

        link.addEventListener('click', async (event) => {
          if (gebestsCompare.getCount() === 0) {
            event.preventDefault();
            return;
          }
          event.preventDefault();
          await gebestsCompare.goToComparePage();
        });
      });
    },

    async initComparePage() {
      const section = document.querySelector('[data-product-compare-section]');
      if (!section) return;

      if (section.dataset.compareSyncing === 'true') return;

      const urlHandles = parseProductsFromSearch(window.location.search);
      if (urlHandles.length) {
        gebestsCompare.syncStorageFromUrl();
      }

      const desiredHandles = getDesiredHandles();
      if (!desiredHandles.length) return;

      const cartHandles = (section.dataset.cartHandles || '')
        .split(',')
        .map((handle) => handle.trim())
        .filter(Boolean);
      const cartMeta = section.dataset.cartMeta || '';

      if (!handlesMatch(desiredHandles, cartHandles) || (desiredHandles.length && !cartMeta)) {
        section.dataset.compareSyncing = 'true';
        showCompareLoading(section);
        const synced = await syncToCart(getItemsForSync(desiredHandles));
        if (synced) {
          window.location.reload();
        } else {
          section.dataset.compareSyncing = 'false';
        }
        return;
      }

      pruneStorageToRenderedProducts();
      gebestsCompare.enrichItemsFromDom();
      gebestsCompare.applyCategoryData();
      initCompareScroll();
    },

    init() {
      bindCompareButtonDelegation();
      gebestsCompare.initButtons();
      gebestsCompare.initComparePage();
      updateCompareButtons();
      updateCompareBar();
    },

    refreshButtons(root) {
      gebestsCompare.initButtons(root || document);
      updateCompareButtons();
      updateCompareBar();
    },
  };

  window.gebestsCompare = gebestsCompare;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => gebestsCompare.init());
  } else {
    gebestsCompare.init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    gebestsCompare.initButtons(event.target);
    updateCompareButtons();
    updateCompareBar();
    if (event.target.querySelector('[data-product-compare-section]')) {
      gebestsCompare.initComparePage();
    }
  });
})();
